// Customer banner — sits atop any customer-context screen (customer detail,
// quote builder, order detail later). Per v8 mockup `.cust-banner` + spec/14:
//
//   - Navy gradient background, white text
//   - Big company name + tier badge inline
//   - Optional meta row (quote #, status pill, RFQ ref, due date)
//   - Right side: Edit / Switch actions (or custom rightActions slot)
//   - Bottom: clean grid of contact/email/phone/rep/terms/RFQ# fields
//
// Pure presentational — caller wires the action handlers + decides which
// meta fields are relevant for the surface (the detail page won't pass
// quoteNumber; the quote builder will).

import type { ReactNode } from 'react';
import type { Customer } from '@/shared/crmTypes';
import { TierBadge } from './TierBadge';

export interface CustomerBannerProps {
  customer: Customer;
  /** Quote # to surface in meta row (quote builder context). */
  quoteNumber?: string;
  /** Status pill text (e.g. 'draft', 'sent'). */
  quoteStatus?: string;
  /** Customer's RFQ reference number. */
  rfqRef?: string;
  /** Edit-customer click. If undefined, button is hidden. */
  onEdit?: () => void;
  /** Switch-customer click (quote builder context). If undefined, button is hidden. */
  onSwitch?: () => void;
  /** Custom action slot — rendered to the right of (or in place of) the standard buttons. */
  rightActions?: ReactNode;
}

/** Primary contact = first contact flagged primary, fall back to first contact. */
function pickPrimaryContact(customer: Customer) {
  if (!customer.contacts || customer.contacts.length === 0) return null;
  return customer.contacts.find((c) => c.primary) ?? customer.contacts[0];
}

export function CustomerBanner({
  customer,
  quoteNumber,
  quoteStatus,
  rfqRef,
  onEdit,
  onSwitch,
  rightActions,
}: CustomerBannerProps) {
  const primary = pickPrimaryContact(customer);

  const hasMeta = quoteNumber || quoteStatus;

  return (
    <div className="bg-gradient-to-br from-navy to-[#2A3B52] text-white rounded shadow px-5 py-4 mb-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold truncate">{customer.companyName}</h1>
            <TierBadge tier={customer.tier} size="md" />
            {customer.code && (
              <span className="text-xs font-mono opacity-70">{customer.code}</span>
            )}
          </div>
          {hasMeta && (
            <div className="text-xs opacity-85 mt-1 flex items-center gap-2 flex-wrap">
              {quoteNumber && <span className="font-mono">{quoteNumber}</span>}
              {quoteNumber && quoteStatus && <span className="opacity-60">·</span>}
              {quoteStatus && (
                <span className="inline-block px-2 py-0.5 bg-white/15 rounded text-[11px] font-semibold uppercase tracking-wide">
                  {quoteStatus}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rightActions}
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="text-xs px-2.5 py-1 rounded border border-white/30 hover:bg-white/10 transition-colors"
            >
              ✏ Edit customer
            </button>
          )}
          {onSwitch && (
            <button
              type="button"
              onClick={onSwitch}
              className="text-xs px-2.5 py-1 rounded border border-white/30 hover:bg-white/10 transition-colors"
            >
              ↔ Switch customer
            </button>
          )}
        </div>
      </div>

      {/* Field grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2 mt-3">
        <BannerField label="Contact" value={primary?.name} />
        <BannerField label="Email" value={primary?.email} mono />
        <BannerField label="Phone" value={primary?.phone} mono />
        <BannerField label="Rep" value={customer.rep} />
        <BannerField label="Terms" value={customer.terms} />
        <BannerField label="RFQ #" value={rfqRef} mono />
      </div>
    </div>
  );
}

interface BannerFieldProps {
  label: string;
  value?: string;
  mono?: boolean;
}

function BannerField({ label, value, mono }: BannerFieldProps) {
  return (
    <div className="text-[11px] min-w-0">
      <div className="opacity-70 uppercase tracking-wide mb-0.5">{label}</div>
      <div className={`font-semibold truncate ${mono ? 'font-mono' : ''}`} title={value ?? ''}>
        {value && value.length > 0 ? value : <span className="opacity-50">—</span>}
      </div>
    </div>
  );
}
